import {
  Flex,
  Icon,
  Box,
  Button,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { Trash01 } from '@kula-ai/icons/dual-tone/Trash01';
import { SharedWith } from './SharedWith';
import { useState, useEffect } from 'react';
import { SharedEmailEmptyPlaceholder } from '@/assets/icons/settings';
import { CreateUpdateDrawer } from './CreateUpdateDrawer';
import { RenderVerification } from './RenderVerification';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import DeleteModal from './DeleteModal';
import { SHARED_EMAIL_API_SERVICES } from '../api';
import { useMutation } from '@tanstack/react-query';
import { useShowToast } from '@/hooks';
import { createContext } from 'react';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { VERIFICATION_STATUS, MX_PROVIDERS } from './constants';
interface SharedEmailData {
  shared_emails?: Array<{
    id: string | number;
    domain: string | number;
    is_primary?: boolean;
    [key: string]: any;
  }>;
  domains?: Array<{
    id: string | number;
    domain: string;
    [key: string]: any;
  }>;
}

interface CreateUpdateEmailDrawerActions {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}
interface RenderSharedEmailCardProps {
  key?: string | number;
  domainDetails: any;
  sharedEmailData: SharedEmailData;
  refetch?: () => void;
}

interface SharedEmailCardProps {
  createUpdateEmailDrawerActions: CreateUpdateEmailDrawerActions;
  sharedEmailData?: SharedEmailData;
  refetch?: () => void;
}
export const domainValueContext = createContext(null);

const RenderSharedEmailCard: React.FC<RenderSharedEmailCardProps> = ({
  key,
  domainDetails,
  sharedEmailData,
  refetch,
}) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [triggerRef, setTriggerRef] = useState(null);
  const [selectedEmailData, setSelectedEmailData] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const toast = useShowToast();

  const cardPopoverActions = useDisclosure();
  const deleteActions = useDisclosure();

  // Check if any email associated with this domain is not verified
  const anyEmailNotVerified = sharedEmailData?.shared_emails
    .filter((email) => email.domain === domainDetails?.id)
    .some((email) => !email.is_verified);

  useEffect(() => {
    checkDomainVerificationStatus(domainDetails?.id);
  }, [sharedEmailData]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkDomainVerificationStatus = async (id) => {
    try {
      const response = sharedEmailData;

      // Find the domain with matching id in the response
      const currentDomain = response?.domains?.find(
        (domain) => domain.id === id
      );

      // If domain is found and verification is no longer in progress
      if (
        currentDomain &&
        currentDomain.verification_status !== VERIFICATION_STATUS.IN_PROGRESS
      ) {
        setIsVerifying(false);
      } else {
        // If still in progress, keep verifying state true
        setIsVerifying(true);
      }
    } catch (error) {
      console.error('Verification status check error:', error);
    }
  };

  const handleDeleteDomain = (id) => {
    setTriggerRef('domain');
    setDeleteId(id);
    deleteActions.onOpen();
  };

  const handleDeleteEmail = (id) => {
    setTriggerRef('email');
    setDeleteId(id);
    deleteActions.onOpen();
  };

  const emailsForDomain = sharedEmailData.shared_emails.filter(
    (email) => email.domain === domainDetails?.id
  );

  const makeDefaultEmailMutation = useMutation({
    mutationFn: (data) => SHARED_EMAIL_API_SERVICES.setDefaultSharedEmail(data),
    onSuccess: (data) => {
      // @ts-ignore
      if (data === 'ok') {
        toast({
          title: 'Default email updated successfully',
          status: 'success',
        });
        refetch();
      } else {
        toast({
          title: 'Failed to update default email',
          status: 'error',
        });
      }
    },
  });

  const verifyDomainMutation = useMutation({
    mutationFn: async (data) => {
      return await SHARED_EMAIL_API_SERVICES.verifyDomain(data);
    },
  });

  const handleMakeDefaultEmail = (id) => {
    makeDefaultEmailMutation.mutate(id);
  };

  const handleVerifyDomain = async (id) => {
    try {
      const response = await verifyDomainMutation.mutateAsync(id);
      // @ts-ignore
      if (response?.status === 'ok') {
        setIsVerifying(true);
        toast({
          title:
            'Shared email verification is in progress. You will be notified once it is done.',
          status: 'message',
        });
        await new Promise((resolve) => setTimeout(resolve, 3000));
        refetch();
      }
    } catch (error) {
      console.error('Verification error:', error);
      setIsVerifying(false);
      toast({
        title: 'Failed to verify Shared email',
        status: 'error',
      });
    }
  };
  const { domain, id, is_primary, is_verified, verification_status } =
    domainDetails || {};

  const forwardRuleNeededDomains = [
    MX_PROVIDERS.GOOGLE,
    MX_PROVIDERS.MICROSOFT,
    MX_PROVIDERS.UNKNOWN,
  ];

  return (
    <domainValueContext.Provider
      value={{ domainDetails: domainDetails, sharedEmailData: sharedEmailData }}
    >
      <Flex
        flexDir="column"
        border="1px solid #EAEAEA"
        borderRadius="6px"
        boxShadow="0px 2px 4px rgba(0, 0, 0, 0.05)"
        key={key}
        mx="auto"
        w="700px"
        gap="sm"
      >
        <Flex
          borderBottom="1px solid #EBEBEB"
          p="1.125em 1em"
          background="secondary-25"
          borderTopLeftRadius="6px"
          borderTopRightRadius="6px"
          gap="6px"
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex alignItems="center">
            <KText size="sm-medium" color="secondary-900" mr="6px">
              {domain}
            </KText>
            {is_primary && (
              <KText size="sm-medium" color="secondary-500">
                (Non-editable)
              </KText>
            )}
            {is_verified && !is_primary && (
              <KText
                background="pastel-2-light"
                p="2px 6px"
                borderRadius="3px"
                textAlign="center"
                w="fit-content"
                size="sm-medium"
                color="pastel-2-dark"
                ml="4px"
              >
                Verified
              </KText>
            )}
            {!is_verified && !is_primary && (
              <Flex
                background="pastel-1-light"
                p="2px 6px"
                borderRadius="3px"
                alignItems={'center'}
                justifyContent="center"
                gap="6px"
                position="relative"
                role="group"
              >
                <KText
                  textAlign="center"
                  w="fit-content"
                  size="sm-medium"
                  color="pastel-1-dark"
                >
                  Domain not validated
                </KText>
                <Tooltip
                  arrowSize={8}
                  shouldWrapChildren
                  hasArrow
                  placement="top"
                  borderRadius="4px"
                  label="Domains that are not validated will be automatically removed after 30 days, and any associated emails will be deleted"
                  width={'280px'}
                >
                  <Icon
                    as={InfoCircle}
                    color="pastel-1-dark"
                    w="16px"
                    h="16px"
                    position="relative"
                  ></Icon>
                </Tooltip>
              </Flex>
            )}
          </Flex>
          {!is_primary && (
            <Icon
              as={Trash01}
              cursor="pointer"
              _hover={{ color: 'secondary-900' }}
              onClick={() => handleDeleteDomain(id)}
            />
          )}
        </Flex>
        {emailsForDomain.map((emailData) => (
          <>
            <Flex
              p="1.125em"
              pt="1em"
              pb="0.75em"
              bg="base-white"
              borderBottomLeftRadius="6px"
              borderBottomRightRadius="6px"
              flexDir="column"
            >
              <Flex
                alignItems="center"
                justifyContent="space-between"
                w="100%"
                role="group"
              >
                <Flex alignItems="center" gap="6px">
                  <TruncatedTextWithTooltip
                    maxW={'150px'}
                    toolTipVariant="default"
                    mr="4px"
                    textTransform="capitalize"
                    text={emailData?.name}
                    variant="heading_3"
                  />
                  <KText size="sm-medium" color="secondary-900" ml="2px">
                    ({emailData?.email})
                  </KText>
                  {!emailData?.is_verified && !domainDetails?.is_primary && (
                    <Flex
                      background="pastel-1-light"
                      p="2px 6px"
                      borderRadius="3px"
                      alignItems={'center'}
                      justifyContent="center"
                      gap="6px"
                    >
                      <KText
                        textAlign="center"
                        w="fit-content"
                        size="sm-medium"
                        color="pastel-1-dark"
                      >
                        {(() => {
                          const domain = sharedEmailData?.domains?.find(
                            (d) => d.id === emailData?.domain
                          );
                          return (
                            domain &&
                            forwardRuleNeededDomains?.includes(
                              domain.mx_provider
                            )
                          );
                        })()
                          ? 'Forwarding rule needed'
                          : 'MX records needed'}
                      </KText>
                    </Flex>
                  )}
                  {emailData?.is_default ? (
                    <KText
                      background="pastel-2-light"
                      p="2px 6px"
                      borderRadius="3px"
                      textAlign="center"
                      w="fit-content"
                      size="sm-medium"
                      color="pastel-2-dark"
                      ml="4px"
                    >
                      Default
                    </KText>
                  ) : emailData?.is_verified ? (
                    <Tooltip
                      arrowSize={8}
                      shouldWrapChildren
                      hasArrow
                      placement="top"
                      borderRadius="4px"
                      label="Default email addresses are used for candidate-facing system emails, including assessment, referral, and application submission emails"
                      width={'280px'}
                    >
                      <Button
                        border="1px solid"
                        borderColor="pastel-2-dark"
                        p="2px 6px"
                        borderRadius={'4px'}
                        color="pastel-2-dark"
                        cursor="pointer"
                        height="24px"
                        background="white"
                        _hover={{
                          background: 'white',
                        }}
                        isLoading={
                          makeDefaultEmailMutation?.isPending &&
                          // @ts-ignore
                          makeDefaultEmailMutation?.variables === emailData?.id
                        }
                        fontWeight="normal"
                        onClick={() => handleMakeDefaultEmail(emailData?.id)}
                        opacity="0"
                        visibility="hidden"
                        _groupHover={{
                          opacity: '1',
                          visibility: 'visible',
                        }}
                      >
                        Make default
                      </Button>
                    </Tooltip>
                  ) : null}
                </Flex>
                <Flex position="relative" top="3px">
                  <Icon
                    as={Edit05}
                    cursor="pointer"
                    position={'relative'}
                    top="3px"
                    _hover={{ color: 'secondary-900' }}
                    mr="0.75em"
                    onClick={async () => {
                      await setOpenDrawer(true);
                      setSelectedEmailData(emailData);
                    }}
                  />
                  {!emailData?.is_primary && !emailData?.is_default && (
                    <Icon
                      as={Trash01}
                      cursor="pointer"
                      position={'relative'}
                      top="3px"
                      _hover={{ color: 'secondary-900' }}
                      mr="8px"
                      onClick={() => handleDeleteEmail(emailData?.id)}
                    />
                  )}
                  <Box position="relative" bottom="1.5px">
                    <SharedWith domainDetails={emailData} />
                  </Box>
                </Flex>
              </Flex>
            </Flex>
            <Box
              borderBottom="1px solid"
              borderColor={'border-color'}
              w="100%"
            ></Box>
          </>
        ))}
        {(anyEmailNotVerified || !is_verified) && !is_primary && (
          <Flex
            alignItems="center"
            justifyContent="right"
            w="100%"
            mx="-1em"
            p="0px"
            my="4px"
            gap="6px"
            opacity={emailsForDomain.length === 0 ? 0.3 : 1}
          >
            <Tooltip
              label={`Add an email address for ${domain} to begin the verification process`}
              isDisabled={emailsForDomain.length !== 0}
              shouldWrapChildren
            >
              <Flex gap="6px">
                <RenderVerification
                  popoverActions={cardPopoverActions}
                  key={key}
                  domainId={id}
                  refetch={refetch}
                  domainDetails={domainDetails}
                  sharedEmailData={sharedEmailData}
                  hasEmails={emailsForDomain.length !== 0}
                  pointerEvents={emailsForDomain.length === 0 ? 'none' : 'auto'}
                />
                <Tooltip
                  label={
                    isVerifying
                      ? 'This usually takes a few minutes. You will get a notification once it is complete.'
                      : ''
                  }
                  placement="top"
                  hasArrow
                  shouldWrapChildren
                >
                  <Button
                    mt="0.75rem"
                    variant="tertiary"
                    h="32px"
                    onClick={() => handleVerifyDomain(id)}
                    isLoading={
                      verifyDomainMutation?.isPending ||
                      isVerifying ||
                      verification_status === 'pending'
                    }
                    loadingText="Verifying"
                    pointerEvents={
                      emailsForDomain.length === 0 ? 'none' : 'auto'
                    }
                  >
                    Verify
                  </Button>
                </Tooltip>
              </Flex>
            </Tooltip>
          </Flex>
        )}
      </Flex>

      <CreateUpdateDrawer
        createUpdateEmailDrawerActions={{
          isOpen: openDrawer,
          onClose: () => setOpenDrawer(false),
        }}
        domainDetails={selectedEmailData}
        sharedEmailData={sharedEmailData}
        refetch={refetch}
      />
      {deleteActions.isOpen && (
        <DeleteModal
          deleteModalActions={deleteActions}
          id={deleteId}
          triggerRef={triggerRef}
          refetch={refetch}
        />
      )}
    </domainValueContext.Provider>
  );
};

export const SharedEmailCard: React.FC<SharedEmailCardProps> = ({
  createUpdateEmailDrawerActions,
  sharedEmailData,
  refetch,
}) => {
  const transformedSharedEmails = sharedEmailData?.shared_emails?.map(
    (email) => {
      // Find the matching domain
      const matchingDomain = sharedEmailData?.domains?.find(
        (domain) => domain.id === email.domain
      );

      return {
        ...email,
        domain_name: matchingDomain ? matchingDomain.domain : null,
        name: email.name || '',
        email: email.email || '',
        is_default: email.is_default || false,
        is_primary: email.is_primary || false,
      };
    }
  );

  // Emails that are not primary
  const nonPrimaryEmails = transformedSharedEmails?.filter(
    (email) => !email.is_primary
  );

  return (
    <Flex flexDir="column" mb="16px" h="100%" w="100%">
      {sharedEmailData?.domains.map((domain) => (
        <Flex
          key={domain?.id}
          flexDir="column"
          mb="16px"
          px="24px"
          py="0"
          pt="2px"
        >
          <RenderSharedEmailCard
            key={domain?.id}
            domainDetails={domain}
            sharedEmailData={sharedEmailData}
            refetch={refetch}
          />
        </Flex>
      ))}

      {/* Only show empty state if NO emails exist at all */}
      {nonPrimaryEmails?.length === 0 && (
        <Flex
          alignItems="center"
          justifyContent="center"
          mb="48px"
          h="100%"
          w="100%"
        >
          <Flex flexDir="column" gap="0.75rem" textAlign="center">
            <Icon
              as={SharedEmailEmptyPlaceholder}
              w="138px"
              h="123px"
              alignSelf="center"
            />
            <KText
              textTransform="capitalize"
              size="xs"
              textAlign="center"
              mt="0.75rem"
            >
              NO CUSTOM EMAILS YET
            </KText>
            <KText
              textTransform="capitalize"
              size="md-medium"
              color="secondary-900"
              textAlign="center"
            >
              Add your first custom email here
            </KText>
            <Button
              w="fit-content"
              alignSelf="center"
              onClick={() => createUpdateEmailDrawerActions?.onOpen()}
            >
              Add Email
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
